// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Server -> Deployment mapping
// ---------------------------------------------------------------------------
// The problem this screen exists to solve: a tenant holds thousands of imported
// servers (ITComponent fact sheets carrying vcpu / ramGb / storageGb / os /
// cluster / datacenter / dnsName / folder). One live tenant has 7,305. Nobody is
// hand-mapping that.
//
// And mapping a server to an APPLICATION is not enough. It has to land on a
// specific DEPLOYMENT — Epic PRD, Epic TST, Epic TRN and one analyst's one-off
// build are four separate migratable units with four different cutover dates and
// four different risk profiles. "Which app" is not a unit of work; "which
// instance" is.
//
// Four ways in, because real engagements use all four:
//   Tab 1 Rules              — ordered, first-match-wins pattern rules.
//   Tab 2 Preview & apply    — nothing is ever written without this step.
//   Tab 3 Coverage           — the honest scoreboard, led by what is MISSING.
//   Tab 4 Worksheet round-trip — export .xlsx, client fills it in (possibly off
//                              paper), upload it back. Idempotent by fact-sheet
//                              id, with a diff before anything is written.
//
// MEASURED GROUND TRUTH from the 7,305-server tenant, which shapes the whole UI:
//   * only ~10.7% of servers (782) carry ANY environment token in their name.
//     6,523 carry none. So hostname rules are NOT the primary path and this
//     screen must never pretend otherwise.
//   * datacenter is populated on 7,305 / 7,305 (100%) and cluster on 7,295
//     (99.9%), and those values literally contain the workload: MDC-CITRIX-EPIC,
//     art-citrix-epic-cluster02, usb-citrix-epic-cluster01. Infrastructure
//     grouping identifies the workload far better than the hostname does.
//   This screen therefore treats cluster and datacenter as first-class match
//   fields, surfaces the tenant's real signal availability live (computed, not
//   hardcoded), and leads the Coverage tab with the unmapped count.
//
// HONESTY POSTURE (non-negotiable, mirrors RelationshipWizard.js / AiEnrichment.js):
//   * rules are NEVER auto-applied — preview first, always;
//   * unmapped servers are never hidden and are always exportable, because the
//     unmapped list IS the work list;
//   * a pattern guess is never presented as a confirmed fact: relationSource,
//     the exact rule that fired, and a confidence are stamped onto every
//     relation and surfaced back in the UI, so a bad rule can be traced and
//     reversed;
//   * an existing mapping is never clobbered without an explicit confirmation;
//   * "revert last apply" exists because bulk operations over 7,000 rows need an
//     escape hatch.
//
// PERFORMANCE: every server is indexed ONCE (lowercased text + pre-split token
// lists per field), and all matching happens inside useMemo. Render is capped at
// 200 rows per page. Nothing iterates the estate during render.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, LinearProgress, MenuItem, Pagination, Paper, Stack, Switch, Table,
  TableBody, TableCell, TableHead, TableRow, Tab, Tabs, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UndoIcon from '@mui/icons-material/Undo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as XLSX from 'xlsx';
import { PICKLISTS } from './schema';
import { exportCsv } from './exports';
import store from './store';

// Optimum palette
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD, whiteSpace: 'nowrap' } };

// Rendering guard. Selection and every count always cover the FULL matching set;
// this only limits how many <TableRow>s exist at once.
const PAGE_SIZE = 200;
const SAMPLE_CAP = 25;

const LS_RULES = (t) => `primordia.serverMapping.rules.${t || 'default'}`;
const LS_JOURNAL = (t) => `primordia.serverMapping.journal.${t || 'default'}`;

const ENVIRONMENTS = PICKLISTS.environment;

// ---------------------------------------------------------------------------
// Token matching — the part that is easy to get dangerously wrong
// ---------------------------------------------------------------------------
// Environment abbreviations MUST be matched as delimited tokens, never as bare
// substrings. A naive indexOf('dr') matches "SQLDRIVER"; a naive indexOf('p')
// matches "APPSERVER"; a naive indexOf('t') matches almost everything. Each of
// those would sweep dozens of unrelated hosts into a Production or DR deployment
// and the mistake would be invisible until cutover weekend.
//
// So a name is TOKENISED first — split on every non-alphanumeric delimiter, on
// every camelCase boundary, and on every letter<->digit transition — and a token
// only matches on exact equality:
//   "EPIC-PRD-01"        -> epic, prd, 01                 ('prd' matches)
//   "epicPrd01"          -> epic, prd, 01, epicprd01      ('prd' matches)
//   "SQLDRIVER"          -> sqldriver                     ('dr'  does NOT match)
//   "APPSERVER"          -> appserver                     ('p'   does NOT match)
//   "BackupPlaceHolder"  -> backup, place, holder, backupplaceholder
// The undelimited whole-string token is kept too, so a hand-typed pattern that
// happens to equal the full blob still works.
const TOKEN_CACHE = new Map();

export function nameTokens(str) {
  const s = String(str || '');
  if (!s) return [];
  const cached = TOKEN_CACHE.get(s);
  if (cached) return cached;
  const spaced = s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')        // camelCase boundary
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')        // letter -> digit
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')        // digit -> letter
    .toLowerCase();
  const split = spaced.split(/[^a-z0-9]+/).filter(Boolean);
  const raw = s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const out = [...new Set([...split, ...raw])];
  if (TOKEN_CACHE.size < 60000) TOKEN_CACHE.set(s, out);
  return out;
}

// Exact token equality by default. `loose` additionally allows the pattern to be
// the TAIL of a longer alphabetic token, because "vmepicprd01" (no delimiter at
// all) is a real convention — but NEVER for a single character, since 't' as a
// suffix would match "vmreport" and quietly file a reporting box under Test.
export function hasToken(tokens, token, loose) {
  if (!token) return false;
  if (tokens.indexOf(token) >= 0) return true;
  if (!loose || token.length < 2) return false;
  for (let i = 0; i < tokens.length; i += 1) {
    const x = tokens[i];
    if (x.length > token.length && /^[a-z]+$/.test(x) && x.endsWith(token)) return true;
  }
  return false;
}

// Environment abbreviations seen across health-system estates. These are
// SUGGESTIONS ONLY — nothing here is ever applied without the user adding the
// rule and running the preview. Single-letter tokens are marked risky: they are
// genuinely used ("EPICP01"), and they are also the ones most likely to produce
// a silently wrong mapping, so they are shown with a warning and default to
// strict (non-loose) matching.
export const ENV_TOKEN_SETS = [
  { env: 'Production',          tokens: ['prd', 'prod', 'production', 'p'] },
  { env: 'Test',                tokens: ['tst', 'test', 't'] },
  { env: 'Development',         tokens: ['dev', 'devl', 'd'] },
  { env: 'Training',            tokens: ['trn', 'train', 'trng', 'tr'] },
  { env: 'Staging / Pre-Prod',  tokens: ['stg', 'stage', 'staging', 'preprod'] },
  { env: 'Disaster Recovery',   tokens: ['dr', 'drp', 'drsite'] },
  { env: 'Isolated Recovery',   tokens: ['ire', 'irz', 'isolated'] },
  { env: 'Validation',          tokens: ['val', 'valid', 'uat', 'qa'] },
  { env: 'Reporting',           tokens: ['rpt', 'report', 'rptg'] },
  { env: 'Sandbox',             tokens: ['sbx', 'sandbox', 'poc'] },
];
const RISKY_TOKENS = new Set(['p', 't', 'd', 'tr', 'dr', 'qa']);
const ALL_ENV_TOKENS = ENV_TOKEN_SETS.reduce((a, s) => a.concat(s.tokens), []);

const ENV_SHORT = {
  Production: 'PRD', 'Disaster Recovery': 'DR', 'Isolated Recovery': 'IRE',
  Test: 'TST', Development: 'DEV', Training: 'TRN', 'Staging / Pre-Prod': 'STG',
  Sandbox: 'SBX', Validation: 'VAL', Reporting: 'RPT', Decommissioned: 'DECOM',
  Other: 'OTH',
};

// Names that suggest a box is already dead. FLAG ONLY — this screen never
// retires anything, it only puts the candidates in front of a human. Short
// tokens are matched as delimited tokens ("...-OLD", "retired-2025-06-10");
// long ones (>= 8 chars) also allow a plain substring, because they show up
// glued together ("BackupPlaceHolder") and are unambiguous at that length.
const DECOMM_TOKENS = [
  'old', 'retire', 'retired', 'decom', 'decomm', 'decommission', 'decommissioned',
  'deprecated', 'obsolete', 'unused', 'dnu', 'donotuse', 'delete', 'deleted',
  'tobedeleted', 'placeholder', 'toretire', 'dead', 'spare',
];

function decommHints(tokens, lowerText) {
  const hits = [];
  for (let i = 0; i < DECOMM_TOKENS.length; i += 1) {
    const t = DECOMM_TOKENS[i];
    if (tokens.indexOf(t) >= 0 || (t.length >= 8 && lowerText.indexOf(t) >= 0)) hits.push(t);
  }
  return hits;
}

// ---------------------------------------------------------------------------
// Match fields. cluster and datacenter come FIRST and are labelled as the
// higher-yield signal, because on real estates they are populated ~100% of the
// time and carry the workload name, while the hostname usually does not.
// ---------------------------------------------------------------------------
export const MATCH_FIELDS = [
  { key: 'cluster',    label: 'Cluster',                 hint: 'usually the strongest signal' },
  { key: 'datacenter', label: 'Datacenter',              hint: 'usually the strongest signal' },
  { key: 'name',       label: 'Server name',             hint: '' },
  { key: 'dnsName',    label: 'DNS name',                hint: '' },
  { key: 'folder',     label: 'Folder / resource group', hint: '' },
  { key: 'any',        label: 'Any of the above',        hint: '' },
];
const TEXT_FIELDS = ['cluster', 'datacenter', 'name', 'dnsName', 'folder'];

const MODES = [
  { key: 'token',     label: 'Tokens' },
  { key: 'substring', label: 'Substring' },
  { key: 'regex',     label: 'Regex' },
];

// Confidence is a HEURISTIC about how much the pattern style can be trusted, not
// a measurement of correctness. It travels with the relation so nothing
// downstream mistakes a guess for a confirmed fact.
const MODE_CONFIDENCE = { token: 0.85, substring: 0.7, regex: 0.6 };

const normKey = (s) => String(s || '').trim().toLowerCase();
const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;
const nowIso = () => new Date().toISOString();
const stamp = () => new Date().toISOString().slice(0, 10);
const deep = (x) => JSON.parse(JSON.stringify(x === undefined ? null : x));

// A server, for this screen, is an ITComponent that came out of an
// infrastructure import. Other ITComponents (a licensed database product, a tech
// standard) are deliberately excluded — counting them would inflate the
// denominator of every percentage on the Coverage tab. The excluded count is
// shown, never hidden.
export function isServerLike(fs) {
  const f = fs.fields || {};
  if (String(f.category || '').toLowerCase() === 'server') return true;
  return ['os', 'vcpu', 'ramGb', 'storageGb', 'dnsName', 'cluster', 'datacenter', 'ipAddress', 'folder']
    .some((k) => f[k] !== undefined && f[k] !== null && f[k] !== '');
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------
let ruleSeq = 0;
const newRuleId = () => `rule-${Date.now().toString(36)}-${(ruleSeq++).toString(36)}`;

export const blankRule = () => ({
  id: newRuleId(),
  enabled: true,
  field: 'cluster',
  mode: 'token',
  pattern: '',
  looseToken: false,
  appId: '',
  appName: '',
  environment: 'Production',
  environmentLabel: '',
  template: '{app} {envShort}',
  confidence: MODE_CONFIDENCE.token,
});

// Compile a rule once per (rules, index) evaluation. Returns { error, test }.
// A regex that will not compile yields a clear message and matches nothing —
// it never silently degrades into a substring search.
export function compileRule(rule) {
  const pattern = String(rule.pattern || '').trim();
  if (!pattern) return { error: 'Pattern is empty.', test: () => false };
  const field = rule.field || 'any';

  if (rule.mode === 'regex') {
    let re;
    try {
      re = new RegExp(pattern, 'i');
    } catch (e) {
      return { error: `Invalid regular expression: ${(e && e.message) || 'could not compile'}`, test: () => false };
    }
    return { error: '', test: (e) => re.test(e.text[field]) };
  }

  if (rule.mode === 'substring') {
    const p = pattern.toLowerCase();
    return { error: '', test: (e) => e.lower[field].indexOf(p) >= 0 };
  }

  // token mode: space-separated tokens, ALL must be present as delimited tokens.
  const tokens = pattern.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return { error: 'Pattern is empty.', test: () => false };
  const loose = !!rule.looseToken;
  return {
    error: '',
    test: (e) => {
      const list = e.tokens[field];
      for (let i = 0; i < tokens.length; i += 1) if (!hasToken(list, tokens[i], loose)) return false;
      return true;
    },
  };
}

export function renderTemplate(tpl, appName, environment, environmentLabel) {
  const env = environment === 'Other' && environmentLabel ? environmentLabel : environment;
  return String(tpl || '{app} {envShort}')
    .replace(/\{app\}/gi, appName || '')
    .replace(/\{envShort\}/gi, ENV_SHORT[environment] || env || '')
    .replace(/\{env\}/gi, env || '')
    .trim() || `${appName} ${env}`;
}

const groupKeyOf = (appId, env, label) =>
  `${appId}||${env}||${env === 'Other' ? normKey(label) : ''}`;

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------
function Kpi({ label, value, sub, tone }) {
  const color = tone === 'bad' ? '#C62828' : tone === 'warn' ? '#B26A00' : NAVY;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 160px' }}>
      <Typography variant="caption" sx={{ color: GREY, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
      {sub ? <Typography variant="caption" sx={{ color: GREY }}>{sub}</Typography> : null}
    </Paper>
  );
}

function ConfidenceChip({ value }) {
  const v = Number(value) || 0;
  const tone = v >= 0.85 ? '#2E7D32' : v >= 0.65 ? '#B26A00' : '#C62828';
  return (
    <Chip size="small" label={pct(v)} variant="outlined"
      sx={{ borderColor: tone, color: tone, fontWeight: 700 }} />
  );
}

function SourceChip({ source }) {
  const map = {
    rule: { label: 'rule', color: BLUE },
    worksheet: { label: 'worksheet', color: '#6A1B9A' },
    manual: { label: 'manual', color: GREY },
  };
  const m = map[source] || { label: source || 'unknown', color: GREY };
  return <Chip size="small" label={m.label} variant="outlined" sx={{ borderColor: m.color, color: m.color, fontWeight: 700 }} />;
}

function Pager({ count, page, onChange }) {
  const pages = Math.ceil(count / PAGE_SIZE);
  if (pages <= 1) return null;
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ color: GREY }}>
        {count.toLocaleString()} rows · showing {PAGE_SIZE} per page (render cap — every count above covers all rows)
      </Typography>
      <Pagination size="small" count={pages} page={page} onChange={(e, p) => onChange(p)} />
    </Stack>
  );
}

// ===========================================================================
export default function ServerMapping({ tenantId, onOpen }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState(0);

  const [apps, setApps] = useState([]);
  const [servers, setServers] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [excludedComponents, setExcludedComponents] = useState(0);

  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState(blankRule);
  const [editingId, setEditingId] = useState(null);
  const [suggestApp, setSuggestApp] = useState(null);
  const [suggestToken, setSuggestToken] = useState('');
  const [pickedSuggestions, setPickedSuggestions] = useState(() => new Set());

  const [excluded, setExcluded] = useState(() => new Set());
  const [accepted, setAccepted] = useState(() => new Set());
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [overwrite, setOverwrite] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [journal, setJournal] = useState(null);
  const [reverting, setReverting] = useState(false);

  const [previewPage, setPreviewPage] = useState(1);
  const [unmappedPage, setUnmappedPage] = useState(1);
  const [provPage, setProvPage] = useState(1);
  const [decommPage, setDecommPage] = useState(1);
  const [wsPage, setWsPage] = useState(1);

  const [wsFile, setWsFile] = useState('');
  const [wsErr, setWsErr] = useState('');
  const [wsDiff, setWsDiff] = useState(null);
  const [wsKeepOther, setWsKeepOther] = useState(true);
  const [wsFilter, setWsFilter] = useState('all');
  const fileRef = useRef(null);
  // The parsed workbook is kept so the "keep unrecognised values as Other"
  // toggle can rebuild the diff in place rather than making the client
  // re-upload the file.
  const lastWorkbook = useRef(null);

  // ---- load ---------------------------------------------------------------
  const reload = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [a, c, d] = await Promise.all([
        store.listFactSheets(tenantId, { type: 'Application' }),
        store.listFactSheets(tenantId, { type: 'ITComponent' }),
        store.listFactSheets(tenantId, { type: 'Deployment' }),
      ]);
      const srv = c.filter(isServerLike);
      setApps(a);
      setServers(srv);
      setDeployments(d);
      setExcludedComponents(c.length - srv.length);
    } catch (e) {
      setErr((e && e.message) || 'Could not load this workspace.');
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { reload(); }, [reload]);

  // Rules and the undo journal are per-workspace and survive a remount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_RULES(tenantId));
      setRules(raw ? JSON.parse(raw) : []);
    } catch (e) { setRules([]); }
    try {
      const raw = localStorage.getItem(LS_JOURNAL(tenantId));
      setJournal(raw ? JSON.parse(raw) : null);
    } catch (e) { setJournal(null); }
    setAccepted(new Set());
    setExcluded(new Set());
    setSummary(null);
    setWsDiff(null);
  }, [tenantId]);

  const persistRules = useCallback((next) => {
    setRules(next);
    try { localStorage.setItem(LS_RULES(tenantId), JSON.stringify(next)); } catch (e) { /* quota */ }
  }, [tenantId]);

  const persistJournal = useCallback((j) => {
    setJournal(j);
    try {
      if (j) localStorage.setItem(LS_JOURNAL(tenantId), JSON.stringify(j));
      else localStorage.removeItem(LS_JOURNAL(tenantId));
    } catch (e) { /* quota */ }
  }, [tenantId]);

  const appById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);
  const appByName = useMemo(() => {
    const m = new Map();
    apps.forEach((a) => { const k = normKey(a.name); if (k && !m.has(k)) m.set(k, a); });
    return m;
  }, [apps]);

  // ---- the index: built ONCE per server load, never during render ----------
  const index = useMemo(() => servers.map((s) => {
    const f = s.fields || {};
    const text = {
      cluster: String(f.cluster || ''),
      datacenter: String(f.datacenter || ''),
      name: String(s.name || f.name || ''),
      dnsName: String(f.dnsName || ''),
      folder: String(f.folder || ''),
    };
    // '\n' rather than ' ' so neither a substring nor a regex can straddle two
    // fields and invent a match that exists in neither.
    text.any = TEXT_FIELDS.map((k) => text[k]).join('\n');
    const lower = {};
    const tokens = {};
    TEXT_FIELDS.forEach((k) => { lower[k] = text[k].toLowerCase(); tokens[k] = nameTokens(text[k]); });
    lower.any = text.any.toLowerCase();
    tokens.any = [...new Set(TEXT_FIELDS.reduce((acc, k) => acc.concat(tokens[k]), []))];
    return {
      id: s.id,
      s,
      name: text.name,
      text,
      lower,
      tokens,
      decomm: decommHints(tokens.any, lower.any),
    };
  }), [servers]);

  const indexById = useMemo(() => new Map(index.map((e) => [e.id, e])), [index]);
  const indexByName = useMemo(() => {
    const m = new Map();
    index.forEach((e) => { const k = normKey(e.name); if (k && !m.has(k)) m.set(k, e); });
    return m;
  }, [index]);

  // ---- what the estate actually gives us to match on ----------------------
  // Computed live per tenant rather than assumed, because it is the single most
  // important thing to tell the user before they write a single rule.
  const signal = useMemo(() => {
    let nameEnv = 0; let infraEnv = 0; let withCluster = 0; let withDc = 0; let withDns = 0;
    const clusters = new Map();
    const dcs = new Map();
    index.forEach((e) => {
      const nameTok = [...e.tokens.name, ...e.tokens.dnsName];
      const infraTok = [...e.tokens.cluster, ...e.tokens.datacenter];
      if (ALL_ENV_TOKENS.some((t) => !RISKY_TOKENS.has(t) && nameTok.indexOf(t) >= 0)) nameEnv += 1;
      if (ALL_ENV_TOKENS.some((t) => !RISKY_TOKENS.has(t) && infraTok.indexOf(t) >= 0)) infraEnv += 1;
      if (e.text.cluster) { withCluster += 1; clusters.set(e.text.cluster, (clusters.get(e.text.cluster) || 0) + 1); }
      if (e.text.datacenter) { withDc += 1; dcs.set(e.text.datacenter, (dcs.get(e.text.datacenter) || 0) + 1); }
      if (e.text.dnsName) withDns += 1;
    });
    const top = (m) => [...m.entries()].map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)).slice(0, 14);
    return {
      total: index.length, nameEnv, infraEnv, withCluster, withDc, withDns,
      topClusters: top(clusters), topDatacenters: top(dcs),
      clusterCount: clusters.size, dcCount: dcs.size,
    };
  }, [index]);

  // ---- what is already mapped --------------------------------------------
  // The link of record is Deployment.relations.servers (one-sided, matching the
  // convention InfrastructureImport.js already uses for app -> itComponents).
  const existingByServer = useMemo(() => {
    const m = new Map();
    deployments.forEach((d) => {
      const df = d.fields || {};
      const appRel = ((d.relations || {}).application || [])[0];
      ((d.relations || {}).servers || []).forEach((r) => {
        if (!r || !r.id) return;
        m.set(r.id, {
          depId: d.id,
          depName: d.name,
          env: df.environment || '',
          envLabel: df.environmentLabel || '',
          appId: (appRel && appRel.id) || '',
          attrs: r.attributes || {},
        });
      });
    });
    return m;
  }, [deployments]);

  // ---- the matcher: first-match-wins over ordered rules -------------------
  const engine = useMemo(() => {
    const compiled = rules.map((r) => ({ r, ...compileRule(r) }));
    const assignment = new Map();     // serverId -> rule index
    const raw = new Array(rules.length).fill(0);   // would match in isolation
    const net = new Array(rules.length).fill(0);   // actually won the server
    const usable = compiled.map((c) => c.r.enabled && !c.error && !!c.r.appId);

    for (let i = 0; i < index.length; i += 1) {
      const e = index[i];
      let winner = -1;
      for (let j = 0; j < compiled.length; j += 1) {
        if (!usable[j]) continue;
        if (!compiled[j].test(e)) continue;
        raw[j] += 1;                          // keep going so shadowing is visible
        if (winner < 0) winner = j;
      }
      if (winner >= 0) { net[winner] += 1; assignment.set(e.id, winner); }
    }
    return { compiled, assignment, raw, net };
  }, [index, rules]);

  // ---- live test of the rule being edited --------------------------------
  const draftTest = useMemo(() => {
    const c = compileRule(draft);
    if (c.error) return { error: c.error, count: 0, sample: [] };
    let count = 0;
    const sample = [];
    for (let i = 0; i < index.length; i += 1) {
      if (!c.test(index[i])) continue;
      count += 1;
      if (sample.length < SAMPLE_CAP) sample.push(index[i]);
    }
    // How many of those a higher rule would already have taken — the difference
    // between "this pattern is right" and "this rule will do anything".
    let shadowed = 0;
    if (rules.length) {
      const above = rules.map((r) => ({ r, ...compileRule(r) }))
        .filter((x) => x.r.enabled && !x.error && x.r.appId && x.r.id !== draft.id);
      if (above.length) {
        for (let i = 0; i < index.length; i += 1) {
          const e = index[i];
          if (!c.test(e)) continue;
          if (above.some((x) => x.test(e))) shadowed += 1;
        }
      }
    }
    return { error: '', count, sample, shadowed };
  }, [draft, index, rules]);

  // ---- preview groups -----------------------------------------------------
  const preview = useMemo(() => {
    const map = new Map();
    let conflicts = 0;
    let already = 0;
    let fresh = 0;

    index.forEach((e) => {
      const j = engine.assignment.get(e.id);
      if (j === undefined) return;
      const r = rules[j];
      const app = appById.get(r.appId);
      if (!app) return;
      const key = groupKeyOf(r.appId, r.environment, r.environmentLabel);
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          appId: r.appId,
          appName: app.name,
          environment: r.environment,
          environmentLabel: r.environmentLabel || '',
          deploymentName: renderTemplate(r.template, app.name, r.environment, r.environmentLabel),
          rows: [],
          ruleIdx: new Set(),
          conflicts: 0,
          already: 0,
          fresh: 0,
          minConfidence: 1,
        };
        map.set(key, g);
      }
      const prior = existingByServer.get(e.id);
      let status = 'new';
      if (prior) {
        status = (prior.appId === r.appId && prior.env === r.environment) ? 'same' : 'conflict';
      }
      if (status === 'conflict') { g.conflicts += 1; conflicts += 1; }
      else if (status === 'same') { g.already += 1; already += 1; }
      else { g.fresh += 1; fresh += 1; }
      g.ruleIdx.add(j);
      g.minConfidence = Math.min(g.minConfidence, Number(r.confidence) || 0);
      g.rows.push({ e, ruleIndex: j, status, prior });
    });

    const groups = [...map.values()].sort((a, b) => b.rows.length - a.rows.length || a.appName.localeCompare(b.appName));
    const unmapped = index.filter((e) => engine.assignment.get(e.id) === undefined && !existingByServer.has(e.id));
    return { groups, unmapped, conflicts, already, fresh, matched: index.length - unmapped.length };
  }, [index, engine, rules, appById, existingByServer]);

  // Only rows that will actually be written: accepted group, not excluded, and
  // either new or (conflict AND overwrite explicitly confirmed).
  const stagedRows = useMemo(() => {
    const out = [];
    preview.groups.forEach((g) => {
      if (!accepted.has(g.key)) return;
      g.rows.forEach((row) => {
        if (excluded.has(row.e.id)) return;
        if (row.status === 'same') return;
        if (row.status === 'conflict' && !overwrite) return;
        out.push({ g, row });
      });
    });
    return out;
  }, [preview, accepted, excluded, overwrite]);

  const stagedConflicts = useMemo(
    () => stagedRows.filter((x) => x.row.status === 'conflict').length,
    [stagedRows]
  );

  // ---- coverage -----------------------------------------------------------
  const coverage = useMemo(() => {
    const total = index.length;
    const mappedIds = new Set();
    const byEnv = new Map();
    const byApp = new Map();
    const byRule = new Map();
    const rows = [];

    deployments.forEach((d) => {
      const df = d.fields || {};
      const appRel = ((d.relations || {}).application || [])[0];
      const app = appRel ? appById.get(appRel.id) : null;
      const appName = (app && app.name) || '(no application linked)';
      const env = df.environment || '(no environment)';
      const list = ((d.relations || {}).servers || []).filter((r) => r && r.id && indexById.has(r.id));
      if (!byApp.has(appName)) byApp.set(appName, { appName, appId: appRel ? appRel.id : '', servers: 0, envs: new Set(), deployments: 0 });
      const ab = byApp.get(appName);
      ab.deployments += 1;
      if (list.length) ab.envs.add(env);
      ab.servers += list.length;
      byEnv.set(env, (byEnv.get(env) || 0) + list.length);
      list.forEach((r) => {
        mappedIds.add(r.id);
        const a = r.attributes || {};
        const ruleKey = `${a.relationSource || 'unknown'}::${a.rule || '(not recorded)'}`;
        if (!byRule.has(ruleKey)) {
          byRule.set(ruleKey, { source: a.relationSource || 'unknown', rule: a.rule || '(not recorded)', count: 0, confidence: a.confidence });
        }
        byRule.get(ruleKey).count += 1;
        rows.push({
          serverId: r.id,
          serverName: (indexById.get(r.id) || {}).name || r.id,
          depId: d.id,
          depName: d.name,
          appName,
          env,
          source: a.relationSource || 'unknown',
          rule: a.rule || '',
          confidence: a.confidence,
          appliedAt: a.appliedAt || '',
        });
      });
    });

    const zeroServerDeployments = deployments.filter(
      (d) => !((d.relations || {}).servers || []).some((r) => r && r.id && indexById.has(r.id))
    );
    const singleEnvApps = [...byApp.values()].filter((a) => a.envs.size === 1 && a.servers > 0);
    const unmappedList = index.filter((e) => !mappedIds.has(e.id));
    const decomm = index.filter((e) => e.decomm.length);

    return {
      total,
      mapped: mappedIds.size,
      unmapped: total - mappedIds.size,
      pctMapped: total ? Math.round((mappedIds.size / total) * 100) : 0,
      byEnv: [...byEnv.entries()].map(([env, count]) => ({ env, count })).sort((a, b) => b.count - a.count),
      byApp: [...byApp.values()].sort((a, b) => b.servers - a.servers || a.appName.localeCompare(b.appName)),
      byRule: [...byRule.values()].sort((a, b) => b.count - a.count),
      rows: rows.sort((a, b) => a.serverName.localeCompare(b.serverName)),
      zeroServerDeployments,
      singleEnvApps,
      unmappedList,
      decomm,
    };
  }, [index, indexById, deployments, appById]);

  // =========================================================================
  // Commit — the ONLY path that writes. Shared by rules-apply and worksheet.
  // =========================================================================
  // items: [{ serverId, appId, environment, environmentLabel, deploymentName,
  //           rule, confidence, source }]
  const commit = useCallback(async (items, meta) => {
    if (!items.length) return;
    setApplying(true);
    setProgress(0);
    setSummary(null);

    const appliedAt = nowIso();
    const beforeSnap = new Map();   // fsId -> relations BEFORE (for revert)
    const createdIds = [];
    const touchedDeps = new Set();
    const touchedApps = new Set();

    const depsWorking = new Map(deployments.map((d) => [d.id, deep(d)]));
    const appsWorking = new Map(apps.map((a) => [a.id, deep(a)]));
    const originalRelations = new Map();
    deployments.forEach((d) => originalRelations.set(d.id, deep(d.relations || {})));
    apps.forEach((a) => originalRelations.set(a.id, deep(a.relations || {})));

    const snap = (id) => {
      if (beforeSnap.has(id) || createdIds.indexOf(id) >= 0) return;
      beforeSnap.set(id, originalRelations.get(id) || {});
    };

    // group the items so we resolve/create each Deployment exactly once
    const byKey = new Map();
    items.forEach((it) => {
      const k = groupKeyOf(it.appId, it.environment, it.environmentLabel);
      if (!byKey.has(k)) byKey.set(k, { ...it, items: [] });
      byKey.get(k).items.push(it);
    });

    let linked = 0; let skipped = 0; let moved = 0; let createdCount = 0; let reused = 0; let failed = 0;
    const errors = [];
    const keys = [...byKey.keys()];

    for (let i = 0; i < keys.length; i += 1) {
      const grp = byKey.get(keys[i]);
      const app = appsWorking.get(grp.appId);
      if (!app) { failed += 1; continue; }

      // ---- resolve or create the Deployment ------------------------------
      let dep = null;
      const wanted = normKey(grp.environmentLabel);
      depsWorking.forEach((d) => {
        if (dep) return;
        const rel = ((d.relations || {}).application || [])[0];
        const df = d.fields || {};
        if (!rel || rel.id !== grp.appId) return;
        if (df.environment !== grp.environment) return;
        if (grp.environment === 'Other' && normKey(df.environmentLabel) !== wanted) return;
        dep = d;
      });

      if (dep) {
        reused += 1;
        snap(dep.id);
      } else {
        try {
          /* eslint-disable-next-line no-await-in-loop */
          const fresh = await store.createFactSheet(app.tenantId || tenantId, 'Deployment', grp.deploymentName);
          dep = deep(fresh);
          dep.fields = {
            ...(dep.fields || {}),
            name: grp.deploymentName,
            environment: grp.environment,
            environmentLabel: grp.environment === 'Other' ? (grp.environmentLabel || '') : '',
            lifecycleState: 'Active',
            description: `Created by Server Mapping (${grp.source}) on ${appliedAt.slice(0, 10)}. `
              + 'Servers on this instance were matched, not confirmed — spot-check before it drives a cutover date.',
          };
          dep.name = grp.deploymentName;
          dep.relations = { ...(dep.relations || {}), application: [{ id: grp.appId }] };
          depsWorking.set(dep.id, dep);
          createdIds.push(dep.id);
          createdCount += 1;
        } catch (e) {
          failed += 1;
          if (errors.length < 5) errors.push(`${grp.deploymentName}: ${(e && e.message) || 'could not create deployment'}`);
          continue;
        }
      }

      dep.relations = dep.relations || {};
      if (!(dep.relations.application || []).length) dep.relations.application = [{ id: grp.appId }];
      const serverRel = [...(dep.relations.servers || [])];
      const have = new Set(serverRel.map((r) => r && r.id));

      // ---- attach the servers --------------------------------------------
      // no-loop-func fires because this callback increments counters declared
      // outside the enclosing for-loop. It is safe here: forEach runs
      // synchronously within this iteration, so nothing captures a variable
      // that has since moved on — the hazard the rule guards against.
      // eslint-disable-next-line no-loop-func
      grp.items.forEach((it) => {
        if (have.has(it.serverId)) { skipped += 1; return; }
        // Move away from a previous deployment only when the caller confirmed it.
        const prior = existingByServer.get(it.serverId);
        if (prior && prior.depId !== dep.id) {
          if (!meta.overwrite) { skipped += 1; return; }
          const old = depsWorking.get(prior.depId);
          if (old) {
            snap(old.id);
            old.relations = old.relations || {};
            old.relations.servers = (old.relations.servers || []).filter((r) => !r || r.id !== it.serverId);
            touchedDeps.add(old.id);
            moved += 1;
          }
        }
        serverRel.push({
          id: it.serverId,
          attributes: {
            // Provenance survives later hand-editing — RelationEditor.js treats
            // these as first-class relation attributes.
            relationSource: it.source,
            rule: it.rule,
            confidence: it.confidence,
            appliedAt,
            notes: `${it.source === 'worksheet' ? 'Client worksheet' : 'Rule'}: ${it.rule} · confidence ${pct(it.confidence)} · ${appliedAt.slice(0, 10)}`,
          },
        });
        have.add(it.serverId);
        linked += 1;
      });
      dep.relations.servers = serverRel;
      touchedDeps.add(dep.id);

      // ---- Application -> deployments ------------------------------------
      snap(app.id);
      app.relations = app.relations || {};
      const depRel = [...(app.relations.deployments || [])];
      if (!depRel.some((r) => r && r.id === dep.id)) {
        depRel.push({
          id: dep.id,
          attributes: {
            relationSource: grp.source,
            rule: grp.rule,
            confidence: grp.confidence,
            appliedAt,
            notes: `Deployment created/linked by Server Mapping (${grp.source}).`,
          },
        });
      }
      app.relations.deployments = depRel;
      touchedApps.add(app.id);

      setProgress(Math.round(((i + 1) / (keys.length + 1)) * 90));
      /* eslint-disable-next-line no-await-in-loop */
      await new Promise((r) => setTimeout(r, 0));
    }

    // ---- write ------------------------------------------------------------
    const toSave = [...touchedDeps].map((id) => depsWorking.get(id))
      .concat([...touchedApps].map((id) => appsWorking.get(id)))
      .filter(Boolean);
    for (let i = 0; i < toSave.length; i += 1) {
      try {
        /* eslint-disable-next-line no-await-in-loop */
        await store.saveFactSheet(toSave[i]);
      } catch (e) {
        failed += 1;
        if (errors.length < 5) errors.push(`${toSave[i].name}: ${(e && e.message) || 'save failed'}`);
      }
      setProgress(90 + Math.round(((i + 1) / toSave.length) * 10));
      /* eslint-disable-next-line no-await-in-loop */
      if (i % 20 === 0) await new Promise((r) => setTimeout(r, 0));
    }

    persistJournal({
      at: appliedAt,
      source: meta.source,
      label: meta.label,
      createdIds,
      before: [...beforeSnap.entries()].map(([id, relations]) => ({ id, relations })),
      counts: { linked, moved, createdCount, reused, skipped },
    });

    setApplying(false);
    setProgress(100);
    setSummary({ linked, moved, createdCount, reused, skipped, failed, errors, source: meta.source });
    setAccepted(new Set());
    await reload();
  }, [apps, deployments, existingByServer, persistJournal, reload, tenantId]);

  // ---- revert -------------------------------------------------------------
  const revertLast = useCallback(async () => {
    if (!journal) return;
    setReverting(true);
    let restored = 0; let removed = 0; let failed = 0;
    for (let i = 0; i < journal.before.length; i += 1) {
      const b = journal.before[i];
      try {
        /* eslint-disable-next-line no-await-in-loop */
        const fs = await store.getFactSheet(b.id);
        if (fs) {
          /* eslint-disable-next-line no-await-in-loop */
          await store.saveFactSheet({ ...fs, relations: b.relations || {} });
          restored += 1;
        }
      } catch (e) { failed += 1; }
    }
    for (let i = 0; i < journal.createdIds.length; i += 1) {
      try {
        /* eslint-disable-next-line no-await-in-loop */
        await store.deleteFactSheet(journal.createdIds[i]);
        removed += 1;
      } catch (e) { failed += 1; }
    }
    persistJournal(null);
    setSummary({ reverted: true, restored, removed, failed, errors: [] });
    setReverting(false);
    await reload();
  }, [journal, persistJournal, reload]);

  // =========================================================================
  // Rule editing
  // =========================================================================
  const setDraftField = (k, v) => setDraft((d) => {
    const next = { ...d, [k]: v };
    if (k === 'mode') next.confidence = MODE_CONFIDENCE[v] || 0.6;
    if (k === 'appId') next.appName = (appById.get(v) || {}).name || '';
    return next;
  });

  const saveDraft = () => {
    if (!draft.pattern.trim() || !draft.appId) return;
    if (compileRule(draft).error) return;
    if (editingId) {
      persistRules(rules.map((r) => (r.id === editingId ? { ...draft, id: editingId } : r)));
      setEditingId(null);
    } else {
      persistRules([...rules, { ...draft, id: newRuleId() }]);
    }
    setDraft({ ...blankRule(), field: draft.field, mode: draft.mode, template: draft.template });
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    const next = [...rules];
    const t = next[i]; next[i] = next[j]; next[j] = t;
    persistRules(next);
  };

  // ---- suggested starter rules (never auto-applied) -----------------------
  const suggestions = useMemo(() => {
    if (!suggestApp) return [];
    const token = (suggestToken || '').trim().toLowerCase();
    if (!token) return [];
    return ENV_TOKEN_SETS.map((set) => {
      const patterns = set.tokens.map((t) => `${token} ${t}`);
      // count how many servers each candidate pattern would hit right now
      let best = { pattern: patterns[0], count: 0, envToken: set.tokens[0] };
      set.tokens.forEach((t, ti) => {
        const rule = { field: 'any', mode: 'token', pattern: patterns[ti], looseToken: false };
        const c = compileRule(rule);
        if (c.error) return;
        let n = 0;
        for (let i = 0; i < index.length; i += 1) if (c.test(index[i])) n += 1;
        if (n > best.count) best = { pattern: patterns[ti], count: n, envToken: t };
      });
      return {
        env: set.env,
        pattern: best.pattern,
        envToken: best.envToken,
        count: best.count,
        risky: RISKY_TOKENS.has(best.envToken),
      };
    }).sort((a, b) => b.count - a.count);
  }, [suggestApp, suggestToken, index]);

  const addSuggestions = () => {
    if (!suggestApp) return;
    const add = suggestions.filter((s) => pickedSuggestions.has(s.env)).map((s) => ({
      ...blankRule(),
      id: newRuleId(),
      field: 'any',
      mode: 'token',
      pattern: s.pattern,
      appId: suggestApp.id,
      appName: suggestApp.name,
      environment: s.env,
      confidence: s.risky ? 0.55 : MODE_CONFIDENCE.token,
    }));
    if (add.length) persistRules([...rules, ...add]);
    setPickedSuggestions(new Set());
  };

  const seedFromValue = (field, value) => {
    setDraft((d) => ({ ...d, field, mode: 'substring', pattern: value, confidence: MODE_CONFIDENCE.substring }));
    setEditingId(null);
  };

  // =========================================================================
  // Worksheet round-trip
  // =========================================================================
  const SHEET_DATA = 'Servers';
  const SHEET_README = 'Read me';
  const SHEET_VALUES = 'Valid values';
  const SHEET_META = '_meta';
  const WS_COLUMNS = [
    { key: 'serverKey', label: 'Server Key (do not edit)' },
    { key: 'serverName', label: 'Server Name' },
    { key: 'dnsName', label: 'DNS Name' },
    { key: 'cluster', label: 'Cluster' },
    { key: 'datacenter', label: 'Datacenter' },
    { key: 'os', label: 'OS' },
    { key: 'vcpu', label: 'vCPU' },
    { key: 'ramGb', label: 'RAM (GB)' },
    { key: 'application', label: 'Application' },
    { key: 'environment', label: 'Environment' },
    { key: 'deploymentName', label: 'Deployment Name' },
    { key: 'notes', label: 'Notes' },
  ];
  const WS_ALIASES = {
    serverKey: ['serverkey', 'serverkeydonotedit', 'key', 'id', 'factsheetid', 'serverid'],
    serverName: ['servername', 'name', 'server', 'hostname', 'host', 'vm', 'vmname'],
    dnsName: ['dnsname', 'dns', 'fqdn'],
    cluster: ['cluster', 'clustername'],
    datacenter: ['datacenter', 'dc', 'datacentre'],
    os: ['os', 'operatingsystem'],
    vcpu: ['vcpu', 'vcpus', 'cpu', 'cpus'],
    ramGb: ['ramgb', 'ram', 'memorygb', 'memory'],
    application: ['application', 'app', 'applicationname', 'targetapplication'],
    environment: ['environment', 'env', 'targetenvironment'],
    deploymentName: ['deploymentname', 'deployment', 'instance', 'instancename'],
    notes: ['notes', 'note', 'comment', 'comments'],
  };

  const exportWorksheet = () => {
    const wb = XLSX.utils.book_new();

    const readme = [
      ['Primordia — Server to Deployment mapping worksheet'],
      [],
      ['Workspace', tenantId],
      ['Generated', nowIso()],
      ['Servers in this worksheet', String(index.length)],
      [],
      ['What we need from you'],
      ['1.', `Go to the "${SHEET_DATA}" sheet. Each ROW is one server we found in your infrastructure export.`],
      ['2.', 'Fill in the Application column with the application that server belongs to.'],
      ['3.', 'Fill in the Environment column — this is the important one. Epic PRD, Epic TST and Epic TRN are separate'],
      ['', 'instances that move on different dates, so a server has to land on the right one, not just the right app.'],
      ['4.', `Allowed Environment values are listed on the "${SHEET_VALUES}" sheet.`],
      ['5.', 'If an instance does not fit any of them (a one-off analyst build, a frozen pre-upgrade clone), type the'],
      ['', 'real name anyway. We will keep it as a named "Other" instance rather than force it into the wrong bucket.'],
      ['6.', 'Deployment Name is optional — leave it blank and we will name the instance for you.'],
      ['7.', 'Leave a row completely blank if you do not know. A blank never erases anything.'],
      [],
      ['Please do NOT'],
      ['', 'Delete or edit column A ("Server Key"). It is hidden, and it is how we match your rows back to the right'],
      ['', 'server even if a name was corrected. If you rebuild this sheet by hand we will fall back to matching on'],
      ['', 'Server Name, and we will tell you exactly how many rows matched which way.'],
      ['', `Rename or delete any sheet — including the hidden "${SHEET_META}" sheet.`],
      [],
      ['What happens when you send it back'],
      ['', 'Nothing is written automatically. We show a row-by-row diff first — unchanged, newly mapped, changed'],
      ['', '(old value -> new value), and rows that matched no server — and a person approves it.'],
    ];
    const wsReadme = XLSX.utils.aoa_to_sheet(readme);
    wsReadme['!cols'] = [{ wch: 6 }, { wch: 118 }];
    XLSX.utils.book_append_sheet(wb, wsReadme, SHEET_README);

    const aoa = [WS_COLUMNS.map((c) => c.label)];
    index.forEach((e) => {
      const f = e.s.fields || {};
      const prior = existingByServer.get(e.id);
      const priorApp = prior ? (appById.get(prior.appId) || {}).name || '' : '';
      aoa.push([
        e.id,
        e.name,
        f.dnsName || '',
        f.cluster || '',
        f.datacenter || '',
        f.os || '',
        f.vcpu || '',
        f.ramGb || '',
        // Pre-filled where a mapping already exists, so exporting and
        // re-importing untouched is a no-op rather than a mass unmapping.
        priorApp,
        prior ? (prior.env === 'Other' && prior.envLabel ? prior.envLabel : prior.env) : '',
        prior ? prior.depName : '',
        '',
      ]);
    });
    const wsData = XLSX.utils.aoa_to_sheet(aoa);
    wsData['!cols'] = WS_COLUMNS.map((c, i) => (
      i === 0 ? { wch: 26, hidden: true } : { wch: Math.min(38, Math.max(14, c.label.length + 6)) }
    ));
    wsData['!freeze'] = { xSplit: 2, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, wsData, SHEET_DATA);

    const valAoa = [['Column', 'Allowed value']];
    ENVIRONMENTS.forEach((v) => valAoa.push(['Environment', v]));
    valAoa.push(['Environment', '(or type the real instance name — we keep it as a named "Other")']);
    apps.forEach((a) => valAoa.push(['Application', a.name]));
    const wsVals = XLSX.utils.aoa_to_sheet(valAoa);
    wsVals['!cols'] = [{ wch: 16 }, { wch: 64 }];
    XLSX.utils.book_append_sheet(wb, wsVals, SHEET_VALUES);

    // Identity map, duplicated outside the visible grid so a client who deletes
    // column A can still be matched on identity rather than on a mutable name.
    const metaAoa = [['kind', 'index', 'key', 'label'], ['form', '', 'tenantId', tenantId],
      ['form', '', 'generatedAt', nowIso()], ['form', '', 'dataSheet', SHEET_DATA]];
    WS_COLUMNS.forEach((c, i) => metaAoa.push(['column', i, c.key, c.label]));
    index.forEach((e, i) => metaAoa.push(['row', i, e.id, e.name]));
    const wsMeta = XLSX.utils.aoa_to_sheet(metaAoa);
    wsMeta['!cols'] = [{ wch: 10 }, { wch: 8 }, { wch: 30 }, { wch: 44 }];
    XLSX.utils.book_append_sheet(wb, wsMeta, SHEET_META);
    wb.Workbook = wb.Workbook || {};
    wb.Workbook.Sheets = wb.SheetNames.map((n) => ({ name: n, Hidden: n === SHEET_META ? 1 : 0 }));

    XLSX.writeFile(wb, `primordia-server-mapping-${normKey(tenantId) || 'tenant'}-${stamp()}.xlsx`);
  };

  const onWorksheetFile = async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setWsErr('');
    setWsDiff(null);
    setWsFile(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      lastWorkbook.current = { wb, fileName: file.name };
      buildWorksheetDiff(wb, file.name, wsKeepOther);
    } catch (e) {
      setWsErr('Could not read that file. Supported: .xlsx, .xls, .csv');
    }
  };

  const buildWorksheetDiff = (wb, fileName, keepOther) => {
    // ---- recover identity from _meta where it survived --------------------
    const metaIdByRow = new Map();
    const metaIdByName = new Map();
    let dataSheet = SHEET_DATA;
    const metaWs = wb.Sheets[SHEET_META];
    if (metaWs) {
      XLSX.utils.sheet_to_json(metaWs, { header: 1, defval: '' }).slice(1).forEach((r) => {
        const kind = String(r[0] || '').toLowerCase();
        const idx = Number(r[1]);
        const key = String(r[2] || '');
        const label = String(r[3] || '');
        if (kind === 'row' && key) {
          if (Number.isFinite(idx)) metaIdByRow.set(idx, key);
          if (label) metaIdByName.set(normKey(label), key);
        } else if (kind === 'form' && key === 'dataSheet' && label) dataSheet = label;
      });
    }

    const ws = wb.Sheets[dataSheet] || wb.Sheets[SHEET_DATA]
      || wb.Sheets[wb.SheetNames.filter((n) => [SHEET_META, SHEET_README, SHEET_VALUES].indexOf(n) < 0)[0]]
      || wb.Sheets[wb.SheetNames[0]];
    if (!ws) { setWsErr(`No "${SHEET_DATA}" sheet in that workbook.`); return; }

    const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (grid.length < 2) { setWsErr('That sheet has no data rows.'); return; }
    const header = (grid[0] || []).map((h) => normKey(h).replace(/[^a-z0-9]/g, ''));
    const colOf = {};
    Object.keys(WS_ALIASES).forEach((k) => {
      const i = header.findIndex((h) => WS_ALIASES[k].indexOf(h) >= 0);
      if (i >= 0) colOf[k] = i;
    });
    if (colOf.application === undefined || colOf.environment === undefined) {
      setWsErr('That sheet has no "Application" and "Environment" columns — nothing to import.');
      return;
    }

    const envByNorm = new Map(ENVIRONMENTS.map((v) => [normKey(v), v]));
    // a couple of forgiving synonyms, still reported as a rename, never silent
    const ENV_SYNONYM = {
      prod: 'Production', prd: 'Production', production: 'Production',
      test: 'Test', tst: 'Test', qa: 'Validation', uat: 'Validation',
      dev: 'Development', development: 'Development', train: 'Training', trn: 'Training',
      stage: 'Staging / Pre-Prod', staging: 'Staging / Pre-Prod', preprod: 'Staging / Pre-Prod',
      dr: 'Disaster Recovery', 'disasterrecovery': 'Disaster Recovery',
      sbx: 'Sandbox', sandbox: 'Sandbox', rpt: 'Reporting', reporting: 'Reporting',
      val: 'Validation', validation: 'Validation',
    };

    const rows = [];
    const counts = { key: 0, name: 0, none: 0, blank: 0, unchanged: 0, new: 0, changed: 0, unknownApp: 0, unknownEnv: 0, dupe: 0 };
    const unknownApps = new Map();
    const unknownEnvs = new Map();
    const seen = new Set();

    grid.slice(1).forEach((r, rowIdx) => {
      if (!r || !r.some((c) => String(c).trim())) return;
      const cell = (k) => (colOf[k] === undefined ? '' : String(r[colOf[k]] === undefined ? '' : r[colOf[k]]).trim());
      const rawKey = cell('serverKey');
      const rawName = cell('serverName');
      const appText = cell('application');
      const envText = cell('environment');
      const depText = cell('deploymentName');
      const notes = cell('notes');

      // ---- identity: key first, then name, and we report which ------------
      let entry = null;
      let matchedBy = 'none';
      if (rawKey && indexById.has(rawKey)) { entry = indexById.get(rawKey); matchedBy = 'key'; }
      if (!entry) {
        const metaId = metaIdByRow.get(rowIdx) || metaIdByName.get(normKey(rawName));
        if (metaId && indexById.has(metaId)) { entry = indexById.get(metaId); matchedBy = 'key'; }
      }
      if (!entry && rawName && indexByName.has(normKey(rawName))) {
        entry = indexByName.get(normKey(rawName)); matchedBy = 'name';
      }

      const row = {
        rowIdx: rowIdx + 2,
        rawKey, rawName, appText, envText, depText, notes,
        matchedBy,
        entry,
        status: 'nomatch',
        issue: '',
        appId: '', appName: '',
        environment: '', environmentLabel: '',
        old: null,
      };

      if (!entry) {
        counts.none += 1;
        row.status = 'nomatch';
        // NEVER create a second server record for a row we could not place.
        row.issue = 'No server in this workspace matches that key or name — nothing will be created for this row.';
        rows.push(row);
        return;
      }
      counts[matchedBy] += 1;

      if (seen.has(entry.id)) {
        counts.dupe += 1;
        row.status = 'duplicate';
        row.issue = 'This server appears on an earlier row too — only the first row is used.';
        rows.push(row);
        return;
      }
      seen.add(entry.id);

      if (!appText && !envText) {
        counts.blank += 1;
        row.status = 'blank';
        rows.push(row);
        return;
      }

      const app = appByName.get(normKey(appText));
      if (!app) {
        counts.unknownApp += 1;
        row.status = 'invalid';
        row.issue = `No application named "${appText}" exists in this workspace.`;
        unknownApps.set(appText, (unknownApps.get(appText) || 0) + 1);
        rows.push(row);
        return;
      }
      row.appId = app.id;
      row.appName = app.name;

      const key = normKey(envText);
      let env = envByNorm.get(key) || '';
      let envLabel = '';
      let coerced = '';
      if (!env && ENV_SYNONYM[key.replace(/[^a-z]/g, '')]) {
        env = ENV_SYNONYM[key.replace(/[^a-z]/g, '')];
        coerced = `read "${envText}" as ${env}`;
      }
      if (!env) {
        counts.unknownEnv += 1;
        unknownEnvs.set(envText, (unknownEnvs.get(envText) || 0) + 1);
        if (keepOther && envText) {
          // "Bob's build" is real information about a real instance. Keep it as
          // a NAMED Other rather than coerce or drop it.
          env = 'Other';
          envLabel = envText;
          coerced = `kept "${envText}" as Other / environmentLabel`;
        } else {
          row.status = 'invalid';
          row.issue = `"${envText}" is not one of the allowed Environment values.`;
          rows.push(row);
          return;
        }
      }
      row.environment = env;
      row.environmentLabel = envLabel;
      row.coerced = coerced;
      row.deploymentName = depText || renderTemplate('{app} {envShort}', app.name, env, envLabel);

      const prior = existingByServer.get(entry.id);
      if (prior) {
        row.old = {
          appName: (appById.get(prior.appId) || {}).name || '(unknown app)',
          env: prior.env === 'Other' && prior.envLabel ? `Other — ${prior.envLabel}` : prior.env,
          depName: prior.depName,
        };
        const sameApp = prior.appId === app.id;
        const sameEnv = prior.env === env && (env !== 'Other' || normKey(prior.envLabel) === normKey(envLabel));
        if (sameApp && sameEnv) { row.status = 'unchanged'; counts.unchanged += 1; }
        else { row.status = 'changed'; counts.changed += 1; }
      } else {
        row.status = 'new';
        counts.new += 1;
      }
      rows.push(row);
    });

    setWsDiff({
      fileName,
      rows,
      counts,
      unknownApps: [...unknownApps.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count),
      unknownEnvs: [...unknownEnvs.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count),
    });
    setWsPage(1);
    setWsFilter('all');
  };

  const wsApplicable = useMemo(
    () => (wsDiff ? wsDiff.rows.filter((r) => r.status === 'new' || r.status === 'changed') : []),
    [wsDiff]
  );

  const applyWorksheet = () => {
    if (!wsDiff || !wsApplicable.length) return;
    setConfirm({
      kind: 'worksheet',
      conflicts: wsDiff.counts.changed,
      total: wsApplicable.length,
    });
  };

  const doApply = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.kind === 'worksheet') {
      const items = wsApplicable.map((r) => ({
        serverId: r.entry.id,
        appId: r.appId,
        appName: r.appName,
        environment: r.environment,
        environmentLabel: r.environmentLabel,
        deploymentName: r.deploymentName,
        rule: `worksheet: ${wsDiff.fileName}${r.matchedBy === 'name' ? ' (matched by name)' : ''}`,
        // A person typed this. It still carries a source and a confidence so
        // nothing downstream has to guess where it came from.
        confidence: r.matchedBy === 'key' ? 1 : 0.9,
        source: 'worksheet',
      }));
      await commit(items, { source: 'worksheet', label: wsDiff.fileName, overwrite: true });
      setWsDiff(null);
      return;
    }
    const items = stagedRows.map(({ g, row }) => {
      const r = rules[row.ruleIndex];
      return {
        serverId: row.e.id,
        appId: g.appId,
        appName: g.appName,
        environment: g.environment,
        environmentLabel: g.environmentLabel,
        deploymentName: g.deploymentName,
        rule: `${r.mode}:${r.field}:${r.pattern}`,
        confidence: Number(r.confidence) || MODE_CONFIDENCE[r.mode] || 0.6,
        source: 'rule',
      };
    });
    await commit(items, { source: 'rule', label: `${items.length} servers`, overwrite });
  };

  // ---- CSV exports --------------------------------------------------------
  const serverCsvColumns = [
    { label: 'Server', value: (e) => e.name },
    { label: 'DNS name', value: (e) => e.text.dnsName },
    { label: 'Cluster', value: (e) => e.text.cluster },
    { label: 'Datacenter', value: (e) => e.text.datacenter },
    { label: 'Folder', value: (e) => e.text.folder },
    { label: 'OS', value: (e) => (e.s.fields || {}).os || '' },
    { label: 'vCPU', value: (e) => (e.s.fields || {}).vcpu || '' },
    { label: 'RAM (GB)', value: (e) => (e.s.fields || {}).ramGb || '' },
    { label: 'Storage (GB)', value: (e) => (e.s.fields || {}).storageGb || '' },
    { label: 'Fact sheet id', value: (e) => e.id },
  ];

  const exportUnmapped = (list, name) =>
    exportCsv(`primordia-${name}-${normKey(tenantId) || 'tenant'}-${stamp()}.csv`, list, serverCsvColumns);

  const exportProvenance = () => exportCsv(
    `primordia-mapping-provenance-${normKey(tenantId) || 'tenant'}-${stamp()}.csv`,
    coverage.rows,
    [
      { label: 'Server', value: 'serverName' },
      { label: 'Deployment', value: 'depName' },
      { label: 'Application', value: 'appName' },
      { label: 'Environment', value: 'env' },
      { label: 'Source', value: 'source' },
      { label: 'Rule', value: 'rule' },
      { label: 'Confidence', value: (r) => (r.confidence === undefined ? '' : pct(r.confidence)) },
      { label: 'Applied at', value: 'appliedAt' },
      { label: 'Server id', value: 'serverId' },
    ]
  );

  // =========================================================================
  // Render
  // =========================================================================
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }} spacing={2}>
        <CircularProgress />
        <Typography variant="body2" sx={{ color: GREY }}>Indexing the estate…</Typography>
      </Stack>
    );
  }

  const draftValid = !!draft.pattern.trim() && !!draft.appId && !draftTest.error;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1, gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: NAVY }}>Server mapping</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>
            {index.length.toLocaleString()} servers · {apps.length.toLocaleString()} applications ·{' '}
            {deployments.length.toLocaleString()} deployments
            {excludedComponents ? ` · ${excludedComponents.toLocaleString()} non-server IT components excluded from every count below` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {journal && (
            <Tooltip title={`Undo the apply of ${new Date(journal.at).toLocaleString()} — ${journal.counts.linked} links, ${journal.counts.createdCount} deployments created`}>
              <span>
                <Button size="small" variant="outlined" startIcon={<UndoIcon />} disabled={reverting || applying}
                  onClick={revertLast} sx={{ borderColor: '#C62828', color: '#C62828' }}>
                  {reverting ? 'Reverting…' : 'Revert last apply'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {applying && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" sx={{ color: GREY }}>Applying… {progress}%</Typography>
        </Box>
      )}

      {summary && (
        <Alert
          severity={summary.failed ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setSummary(null)}
        >
          <AlertTitle>{summary.reverted ? 'Reverted' : 'Applied'}</AlertTitle>
          {summary.reverted ? (
            <>Restored {summary.restored} fact sheet(s) and removed {summary.removed} deployment(s) created by that apply.
              {summary.failed ? ` ${summary.failed} could not be restored.` : ''}</>
          ) : (
            <>
              {summary.linked.toLocaleString()} server(s) linked · {summary.createdCount} deployment(s) created ·{' '}
              {summary.reused} reused · {summary.moved} moved off a previous deployment ·{' '}
              {summary.skipped.toLocaleString()} skipped (already linked, or an existing mapping you chose not to overwrite)
              {summary.failed ? ` · ${summary.failed} failed` : ''}
              {summary.errors && summary.errors.length ? <> — {summary.errors.join('; ')}</> : null}
            </>
          )}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Rules" />
        <Tab label={`Preview & apply${preview.groups.length ? ` (${preview.groups.length})` : ''}`} />
        <Tab label="Coverage" />
        <Tab label="Worksheet round-trip" />
      </Tabs>

      {/* ==================================================================== */}
      {/* TAB 1 — RULES                                                        */}
      {/* ==================================================================== */}
      {tab === 0 && (
        <Stack spacing={2}>
          <Alert severity="info">
            <AlertTitle>Before you write a rule — what this estate will actually let you match on</AlertTitle>
            Only <b>{signal.nameEnv.toLocaleString()} of {signal.total.toLocaleString()}</b>{' '}
            ({signal.total ? Math.round((signal.nameEnv / signal.total) * 100) : 0}%) servers carry any environment token
            in their <b>name or DNS name</b>. Hostname rules alone will not map this estate.{' '}
            <b>Cluster is populated on {signal.withCluster.toLocaleString()}</b> and{' '}
            <b>datacenter on {signal.withDc.toLocaleString()}</b> of {signal.total.toLocaleString()} — and{' '}
            {signal.infraEnv.toLocaleString()} of them carry an environment token there. Infrastructure grouping is
            usually the higher-yield signal, so cluster and datacenter are the first two match fields below.
          </Alert>

          {/* ---- signal seeds ------------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
              The estate&apos;s own groupings ({signal.clusterCount} clusters, {signal.dcCount} datacenters)
            </Typography>
            <Typography variant="caption" sx={{ color: GREY }}>
              Click a value to load it into the rule builder as a substring pattern. These are the real values on your
              servers — several of them name the workload directly.
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Box sx={{ minWidth: 280, flex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: NAVY }}>Top clusters</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {signal.topClusters.map((c) => (
                    <Chip key={c.value} size="small" label={`${c.value} · ${c.count}`} onClick={() => seedFromValue('cluster', c.value)}
                      sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }} />
                  ))}
                  {!signal.topClusters.length && <Typography variant="caption" sx={{ color: GREY }}>No cluster values.</Typography>}
                </Box>
              </Box>
              <Box sx={{ minWidth: 280, flex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: NAVY }}>Top datacenters</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {signal.topDatacenters.map((c) => (
                    <Chip key={c.value} size="small" label={`${c.value} · ${c.count}`} onClick={() => seedFromValue('datacenter', c.value)}
                      sx={{ mr: 0.5, mb: 0.5, cursor: 'pointer' }} />
                  ))}
                  {!signal.topDatacenters.length && <Typography variant="caption" sx={{ color: GREY }}>No datacenter values.</Typography>}
                </Box>
              </Box>
            </Stack>
          </Paper>

          {/* ---- rule builder -------------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
              {editingId ? 'Edit rule' : 'New rule'}
            </Typography>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <TextField select size="small" label="Match against" value={draft.field}
                onChange={(e) => setDraftField('field', e.target.value)} sx={{ minWidth: 220 }}>
                {MATCH_FIELDS.map((f) => (
                  <MenuItem key={f.key} value={f.key}>
                    {f.label}{f.hint ? ` — ${f.hint}` : ''}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <ToggleButtonGroup size="small" exclusive value={draft.mode}
                  onChange={(e, v) => v && setDraftField('mode', v)}>
                  {MODES.map((m) => <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Box>

              <TextField
                size="small" label="Pattern" value={draft.pattern} sx={{ minWidth: 280, flex: 1 }}
                onChange={(e) => setDraftField('pattern', e.target.value)}
                error={!!draft.pattern && !!draftTest.error}
                helperText={
                  draft.pattern && draftTest.error
                    ? draftTest.error
                    : draft.mode === 'token'
                      ? 'Space-separated tokens — ALL must be present, matched at delimiter / camelCase / digit boundaries (so "dr" never matches SQLDRIVER).'
                      : draft.mode === 'substring'
                        ? 'Plain case-insensitive substring. Fine for cluster and datacenter values; risky on hostnames.'
                        : 'JavaScript regular expression, case-insensitive.'
                }
              />
            </Stack>

            {draft.mode === 'token' && (
              <FormControlLabel
                sx={{ mb: 1 }}
                control={<Switch size="small" checked={!!draft.looseToken} onChange={(e) => setDraftField('looseToken', e.target.checked)} />}
                label={
                  <Typography variant="caption" sx={{ color: GREY }}>
                    Also match a token that <b>ends with</b> the pattern (catches undelimited names like <code>vmepicprd01</code>).
                    Never applies to single-character patterns — &quot;t&quot; as a suffix would match <code>vmreport</code>.
                  </Typography>
                }
              />
            )}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Autocomplete
                size="small" sx={{ minWidth: 280, flex: 1 }} options={apps}
                getOptionLabel={(o) => o.name || ''}
                value={appById.get(draft.appId) || null}
                onChange={(e, v) => setDraftField('appId', v ? v.id : '')}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Target application" required />}
              />
              <TextField select size="small" label="Target environment" value={draft.environment}
                onChange={(e) => setDraftField('environment', e.target.value)} sx={{ minWidth: 220 }}>
                {ENVIRONMENTS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              {draft.environment === 'Other' && (
                <TextField size="small" label="Instance name (kept verbatim)" value={draft.environmentLabel}
                  onChange={(e) => setDraftField('environmentLabel', e.target.value)} sx={{ minWidth: 240 }}
                  helperText="e.g. Bob's build — kept rather than forced into a standard bucket" />
              )}
              <TextField size="small" label="Deployment name template" value={draft.template}
                onChange={(e) => setDraftField('template', e.target.value)} sx={{ minWidth: 240 }}
                helperText="{app} {env} {envShort}" />
              <TextField select size="small" label="Confidence" value={String(draft.confidence)}
                onChange={(e) => setDraftField('confidence', Number(e.target.value))} sx={{ minWidth: 150 }}
                helperText="stamped on every link">
                <MenuItem value="0.95">95% — verified</MenuItem>
                <MenuItem value="0.85">85% — strong pattern</MenuItem>
                <MenuItem value="0.7">70% — plausible</MenuItem>
                <MenuItem value="0.6">60% — guess</MenuItem>
                <MenuItem value="0.4">40% — weak, review first</MenuItem>
              </TextField>
            </Stack>

            {/* ---- live test ------------------------------------------- */}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                  Test — before this rule is added
                </Typography>
                {draftTest.error ? (
                  <Chip size="small" color="error" label={draftTest.error} />
                ) : (
                  <Typography variant="body2">
                    <b style={{ fontSize: 18, color: NAVY }}>{draftTest.count.toLocaleString()}</b> of{' '}
                    {index.length.toLocaleString()} servers would match
                    {draftTest.shadowed ? ` · ${draftTest.shadowed.toLocaleString()} of them are already claimed by a rule above (first match wins)` : ''}
                  </Typography>
                )}
              </Stack>
              {!draftTest.error && draftTest.sample.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 190, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow sx={headSx}>
                      <TableCell>Server</TableCell><TableCell>Cluster</TableCell>
                      <TableCell>Datacenter</TableCell><TableCell>DNS</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {draftTest.sample.map((e) => (
                        <TableRow key={e.id} hover>
                          <TableCell>{e.name}</TableCell>
                          <TableCell sx={{ color: GREY }}>{e.text.cluster}</TableCell>
                          <TableCell sx={{ color: GREY }}>{e.text.datacenter}</TableCell>
                          <TableCell sx={{ color: GREY }}>{e.text.dnsName}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {draftTest.count > SAMPLE_CAP && (
                    <Typography variant="caption" sx={{ color: GREY }}>
                      Sample of {SAMPLE_CAP} — full list appears on Preview &amp; apply once the rule is added.
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" startIcon={<AddIcon />} disabled={!draftValid}
                onClick={saveDraft} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
                {editingId ? 'Save rule' : 'Add rule'}
              </Button>
              {editingId && (
                <Button onClick={() => { setEditingId(null); setDraft(blankRule()); }}>Cancel</Button>
              )}
              <Typography variant="caption" sx={{ color: GREY, alignSelf: 'center' }}>
                Adding a rule changes nothing on its own — go to Preview &amp; apply.
              </Typography>
            </Stack>
          </Paper>

          {/* ---- suggested starter rules --------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
              Suggested starter rules — you must confirm each one
            </Typography>
            <Typography variant="caption" sx={{ color: GREY }}>
              Common health-system environment tokens (PRD/PROD/P, TST/TEST/T, DEV/D, TRN/TRAIN, STG/STAGE, DR, VAL,
              RPT, SBX). Nothing here is applied — pick an application, check the environments you believe exist, and
              the rules land in the list below for you to preview. Counts are live against this estate.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, mb: 1 }} flexWrap="wrap" useFlexGap>
              <Autocomplete
                size="small" sx={{ minWidth: 300 }} options={apps} getOptionLabel={(o) => o.name || ''}
                value={suggestApp}
                onChange={(e, v) => {
                  setSuggestApp(v);
                  const first = (v ? v.name : '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ')[0] || '';
                  setSuggestToken(first);
                  setPickedSuggestions(new Set());
                }}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Application" />}
              />
              <TextField size="small" label="Application token" value={suggestToken}
                onChange={(e) => { setSuggestToken(e.target.value); setPickedSuggestions(new Set()); }}
                sx={{ minWidth: 200 }} helperText="the token that identifies this app in names/clusters" />
            </Stack>

            {suggestApp && suggestToken ? (
              <>
                <Table size="small">
                  <TableHead><TableRow sx={headSx}>
                    <TableCell padding="checkbox" />
                    <TableCell>Environment</TableCell><TableCell>Proposed pattern</TableCell>
                    <TableCell align="right">Would match</TableCell><TableCell>Note</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {suggestions.map((s) => (
                      <TableRow key={s.env} hover sx={{ opacity: s.count ? 1 : 0.55 }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={pickedSuggestions.has(s.env)}
                            onChange={(e) => {
                              const next = new Set(pickedSuggestions);
                              if (e.target.checked) next.add(s.env); else next.delete(s.env);
                              setPickedSuggestions(next);
                            }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.env}</TableCell>
                        <TableCell><code>{s.pattern}</code></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: s.count ? NAVY : GREY }}>
                          {s.count.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: GREY, fontSize: 12 }}>
                          {s.risky
                            ? `"${s.envToken}" is a short/ambiguous token — matched strictly at boundaries, but spot-check it`
                            : s.count ? '' : 'no servers carry this token — this environment may genuinely not exist'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ mt: 1, borderColor: BLUE, color: BLUE }}
                  disabled={!pickedSuggestions.size} onClick={addSuggestions}>
                  Add {pickedSuggestions.size} selected rule{pickedSuggestions.size === 1 ? '' : 's'}
                </Button>
              </>
            ) : (
              <Typography variant="caption" sx={{ color: GREY }}>Pick an application to see suggestions.</Typography>
            )}
          </Paper>

          {/* ---- rule list ------------------------------------------------ */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
              Rules — evaluated top to bottom, first match wins
            </Typography>
            {!rules.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                No rules yet. Every server is unmapped until you add one.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell>#</TableCell><TableCell>On</TableCell><TableCell>Enabled</TableCell>
                  <TableCell>Match</TableCell><TableCell>Field</TableCell><TableCell>Pattern</TableCell>
                  <TableCell>Application</TableCell><TableCell>Environment</TableCell>
                  <TableCell align="right">Wins</TableCell><TableCell align="right">Raw</TableCell>
                  <TableCell>Confidence</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rules.map((r, i) => {
                    const c = engine.compiled[i] || {};
                    return (
                      <TableRow key={r.id} hover sx={{ opacity: r.enabled ? 1 : 0.5 }}>
                        <TableCell sx={{ fontWeight: 700 }}>{i + 1}</TableCell>
                        <TableCell>
                          <Stack direction="row">
                            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                            <IconButton size="small" disabled={i === rules.length - 1} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Switch size="small" checked={!!r.enabled}
                            onChange={(e) => persistRules(rules.map((x) => (x.id === r.id ? { ...x, enabled: e.target.checked } : x)))} />
                        </TableCell>
                        <TableCell>{(MODES.find((m) => m.key === r.mode) || {}).label}{r.looseToken ? ' +suffix' : ''}</TableCell>
                        <TableCell>{(MATCH_FIELDS.find((f) => f.key === r.field) || {}).label}</TableCell>
                        <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                          <code>{r.pattern}</code>
                          {c.error ? <Typography variant="caption" sx={{ display: 'block', color: '#C62828' }}>{c.error}</Typography> : null}
                        </TableCell>
                        <TableCell>{(appById.get(r.appId) || {}).name || <span style={{ color: '#C62828' }}>missing</span>}</TableCell>
                        <TableCell>{r.environment}{r.environment === 'Other' && r.environmentLabel ? ` — ${r.environmentLabel}` : ''}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{(engine.net[i] || 0).toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: GREY }}>
                          <Tooltip title="How many servers this rule matches in isolation. A raw count far above the win count means a rule above it is shadowing this one.">
                            <span>{(engine.raw[i] || 0).toLocaleString()}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell><ConfidenceChip value={r.confidence} /></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => { setDraft({ ...r }); setEditingId(r.id); }}><EditIcon fontSize="inherit" /></IconButton>
                          <IconButton size="small" onClick={() => persistRules(rules.filter((x) => x.id !== r.id))}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Stack>
      )}

      {/* ==================================================================== */}
      {/* TAB 2 — PREVIEW & APPLY                                              */}
      {/* ==================================================================== */}
      {tab === 1 && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Kpi label="Unmapped after these rules" value={preview.unmapped.length.toLocaleString()}
              sub="the actual work list" tone={preview.unmapped.length ? 'bad' : undefined} />
            <Kpi label="Proposed groups" value={preview.groups.length.toLocaleString()} sub="application + environment" />
            <Kpi label="New links proposed" value={preview.fresh.toLocaleString()} />
            <Kpi label="Already mapped the same way" value={preview.already.toLocaleString()} sub="no change" />
            <Kpi label="Conflicts with an existing mapping" value={preview.conflicts.toLocaleString()}
              sub="skipped unless you confirm" tone={preview.conflicts ? 'warn' : undefined} />
          </Stack>

          <Alert severity="warning">
            Nothing below has been written. Every proposal here came from a <b>pattern</b>, not from a person who
            knows the estate — accept a group only if you would defend it at a cutover meeting.
          </Alert>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button size="small" variant="outlined" sx={{ borderColor: BLUE, color: BLUE }}
              disabled={!preview.groups.length}
              onClick={() => setAccepted(new Set(preview.groups.map((g) => g.key)))}>
              Accept all {preview.groups.length} groups
            </Button>
            <Button size="small" onClick={() => setAccepted(new Set())} disabled={!accepted.size}>Clear</Button>
            <FormControlLabel
              control={<Checkbox size="small" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} disabled={!preview.conflicts} />}
              label={<Typography variant="body2">
                Overwrite the {preview.conflicts.toLocaleString()} conflicting mapping(s) — moves those servers off their current deployment
              </Typography>}
            />
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" disabled={!stagedRows.length || applying}
              onClick={() => setConfirm({ kind: 'rules', total: stagedRows.length, conflicts: stagedConflicts })}
              sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
              Apply {stagedRows.length.toLocaleString()} link{stagedRows.length === 1 ? '' : 's'}
            </Button>
          </Stack>

          {/* ---- groups ------------------------------------------------- */}
          {!preview.groups.length ? (
            <Alert severity="info">No rule matched anything yet. Add rules on the Rules tab.</Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell padding="checkbox" />
                  <TableCell />
                  <TableCell>Application</TableCell><TableCell>Environment</TableCell>
                  <TableCell>Deployment to create / reuse</TableCell>
                  <TableCell align="right">Servers</TableCell><TableCell align="right">New</TableCell>
                  <TableCell align="right">Same</TableCell><TableCell align="right">Conflict</TableCell>
                  <TableCell>Lowest confidence</TableCell><TableCell>From rule(s)</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {preview.groups
                    .slice((previewPage - 1) * PAGE_SIZE, previewPage * PAGE_SIZE)
                    .map((g) => {
                      const open = openGroups.has(g.key);
                      const excludedHere = g.rows.filter((r) => excluded.has(r.e.id)).length;
                      return (
                        <React.Fragment key={g.key}>
                          <TableRow hover>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={accepted.has(g.key)}
                                onChange={(e) => {
                                  const next = new Set(accepted);
                                  if (e.target.checked) next.add(g.key); else next.delete(g.key);
                                  setAccepted(next);
                                }} />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => {
                                const next = new Set(openGroups);
                                if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                                setOpenGroups(next);
                              }}>
                                {open ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{g.appName}</TableCell>
                            <TableCell>{g.environment}{g.environmentLabel ? ` — ${g.environmentLabel}` : ''}</TableCell>
                            <TableCell sx={{ color: GREY }}>{g.deploymentName}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {g.rows.length.toLocaleString()}
                              {excludedHere ? <Typography variant="caption" sx={{ display: 'block', color: GREY }}>−{excludedHere} excluded</Typography> : null}
                            </TableCell>
                            <TableCell align="right">{g.fresh.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ color: GREY }}>{g.already.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ color: g.conflicts ? '#B26A00' : GREY, fontWeight: g.conflicts ? 700 : 400 }}>
                              {g.conflicts.toLocaleString()}
                            </TableCell>
                            <TableCell><ConfidenceChip value={g.minConfidence} /></TableCell>
                            <TableCell sx={{ color: GREY, fontSize: 12, maxWidth: 220 }}>
                              {[...g.ruleIdx].map((i) => `#${i + 1} ${rules[i].pattern}`).join(', ')}
                            </TableCell>
                          </TableRow>

                          {open && (
                            <TableRow>
                              <TableCell colSpan={11} sx={{ bgcolor: 'action.hover', p: 1 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: GREY }}>
                                    Untick any server that does not belong on this instance. Showing up to {PAGE_SIZE} of {g.rows.length.toLocaleString()}.
                                  </Typography>
                                  <Button size="small" startIcon={<DownloadIcon />}
                                    onClick={() => exportUnmapped(g.rows.map((r) => r.e), `group-${normKey(g.appName)}-${normKey(g.environment)}`)}>
                                    Export this group
                                  </Button>
                                </Stack>
                                <Box sx={{ maxHeight: 380, overflow: 'auto' }}>
                                  <Table size="small">
                                    <TableHead><TableRow sx={headSx}>
                                      <TableCell padding="checkbox">Include</TableCell>
                                      <TableCell>Server</TableCell><TableCell>Cluster</TableCell>
                                      <TableCell>Datacenter</TableCell><TableCell>Matched by</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow></TableHead>
                                    <TableBody>
                                      {g.rows.slice(0, PAGE_SIZE).map((row) => (
                                        <TableRow key={row.e.id} hover>
                                          <TableCell padding="checkbox">
                                            <Checkbox size="small" checked={!excluded.has(row.e.id)}
                                              onChange={(e) => {
                                                const next = new Set(excluded);
                                                if (e.target.checked) next.delete(row.e.id); else next.add(row.e.id);
                                                setExcluded(next);
                                              }} />
                                          </TableCell>
                                          <TableCell>
                                            <Box component="span" sx={{ cursor: onOpen ? 'pointer' : 'default', color: onOpen ? BLUE : 'inherit' }}
                                              onClick={() => onOpen && onOpen(row.e.id)}>
                                              {row.e.name}
                                            </Box>
                                            {row.e.decomm.length ? (
                                              <Chip size="small" label="likely decommission" sx={{ ml: 1, bgcolor: '#FDECEA', color: '#C62828', fontWeight: 700 }} />
                                            ) : null}
                                          </TableCell>
                                          <TableCell sx={{ color: GREY }}>{row.e.text.cluster}</TableCell>
                                          <TableCell sx={{ color: GREY }}>{row.e.text.datacenter}</TableCell>
                                          <TableCell sx={{ color: GREY, fontSize: 12 }}>
                                            #{row.ruleIndex + 1} <code>{rules[row.ruleIndex].pattern}</code>
                                          </TableCell>
                                          <TableCell>
                                            {row.status === 'new' && <Chip size="small" label="new" color="success" variant="outlined" />}
                                            {row.status === 'same' && <Chip size="small" label="already mapped here" variant="outlined" />}
                                            {row.status === 'conflict' && (
                                              <Tooltip title={`Currently on ${row.prior.depName}`}>
                                                <Chip size="small" label="conflict" sx={{ borderColor: '#B26A00', color: '#B26A00' }} variant="outlined" />
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
              <Box sx={{ p: 1 }}>
                <Pager count={preview.groups.length} page={previewPage} onChange={setPreviewPage} />
              </Box>
            </Paper>
          )}

          {/* ---- unmapped bucket — always visible ------------------------ */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                UNMAPPED — {preview.unmapped.length.toLocaleString()} server
                {preview.unmapped.length === 1 ? '' : 's'} no rule touched
              </Typography>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                disabled={!preview.unmapped.length}
                onClick={() => exportUnmapped(preview.unmapped, 'unmapped-servers')}
                sx={{ borderColor: BLUE, color: BLUE }}>
                Export unmapped to CSV
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ color: GREY }}>
              This is the real work list. It stays on screen whether it is 12 rows or 6,500.
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell>Server</TableCell><TableCell>Cluster</TableCell><TableCell>Datacenter</TableCell>
                  <TableCell>DNS</TableCell><TableCell>OS</TableCell><TableCell align="right">vCPU</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {preview.unmapped.slice((unmappedPage - 1) * PAGE_SIZE, unmappedPage * PAGE_SIZE).map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell>
                        <Box component="span" sx={{ cursor: onOpen ? 'pointer' : 'default', color: onOpen ? BLUE : 'inherit' }}
                          onClick={() => onOpen && onOpen(e.id)}>{e.name}</Box>
                      </TableCell>
                      <TableCell sx={{ color: GREY }}>{e.text.cluster}</TableCell>
                      <TableCell sx={{ color: GREY }}>{e.text.datacenter}</TableCell>
                      <TableCell sx={{ color: GREY }}>{e.text.dnsName}</TableCell>
                      <TableCell sx={{ color: GREY }}>{(e.s.fields || {}).os || ''}</TableCell>
                      <TableCell align="right">{(e.s.fields || {}).vcpu || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pager count={preview.unmapped.length} page={unmappedPage} onChange={setUnmappedPage} />
            </Box>
          </Paper>
        </Stack>
      )}

      {/* ==================================================================== */}
      {/* TAB 3 — COVERAGE                                                     */}
      {/* ==================================================================== */}
      {tab === 2 && (
        <Stack spacing={2}>
          {/* Leads with what is MISSING, deliberately. A mapping percentage is
              the most flattering and least useful number on this screen. */}
          <Alert severity={coverage.unmapped ? 'warning' : 'success'}>
            <AlertTitle>
              {coverage.unmapped.toLocaleString()} of {coverage.total.toLocaleString()} servers are still unmapped
            </AlertTitle>
            {coverage.mapped.toLocaleString()} are on a deployment ({coverage.pctMapped}%). Only{' '}
            {signal.nameEnv.toLocaleString()} servers in this estate carry an environment token in their name at all,
            so hostname rules cannot close this gap on their own — the cluster and datacenter values are the higher-yield
            signal, and the rest is human knowledge captured through the worksheet.
          </Alert>

          <Alert severity="info" icon={<WarningAmberIcon />}>
            <AlertTitle>A high percentage is not the same as an accurate mapping</AlertTitle>
            A broad regex can map 90% of an estate in one click and be wrong about most of it. Every link below carries
            the rule that produced it and a confidence — spot-check the largest groups and the lowest confidences first,
            and use <b>Revert last apply</b> the moment a rule looks wrong.
          </Alert>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Kpi label="Unmapped" value={coverage.unmapped.toLocaleString()} tone={coverage.unmapped ? 'bad' : undefined} sub="work list" />
            <Kpi label="Mapped" value={coverage.mapped.toLocaleString()} />
            <Kpi label="Total servers" value={coverage.total.toLocaleString()} />
            <Kpi label="% mapped" value={`${coverage.pctMapped}%`} sub="not a quality measure" />
            <Kpi label="Deployments" value={deployments.length.toLocaleString()}
              sub={`${coverage.zeroServerDeployments.length} with no servers`} tone={coverage.zeroServerDeployments.length ? 'warn' : undefined} />
            <Kpi label="Likely decommission candidates" value={coverage.decomm.length.toLocaleString()}
              sub="flagged only — never retired" tone={coverage.decomm.length ? 'warn' : undefined} />
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Paper variant="outlined" sx={{ p: 2, flex: '1 1 320px' }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>By environment</Typography>
              <Table size="small">
                <TableHead><TableRow sx={headSx}><TableCell>Environment</TableCell><TableCell align="right">Servers</TableCell></TableRow></TableHead>
                <TableBody>
                  {coverage.byEnv.map((r) => (
                    <TableRow key={r.env} hover><TableCell>{r.env}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{r.count.toLocaleString()}</TableCell></TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ color: '#C62828', fontWeight: 700 }}>(unmapped)</TableCell>
                    <TableCell align="right" sx={{ color: '#C62828', fontWeight: 700 }}>{coverage.unmapped.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, flex: '2 1 460px' }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>By application</Typography>
              <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead><TableRow sx={headSx}>
                    <TableCell>Application</TableCell><TableCell align="right">Deployments</TableCell>
                    <TableCell align="right">Servers</TableCell><TableCell>Environments</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {coverage.byApp.slice(0, PAGE_SIZE).map((a) => (
                      <TableRow key={a.appName} hover>
                        <TableCell>{a.appName}</TableCell>
                        <TableCell align="right">{a.deployments}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{a.servers.toLocaleString()}</TableCell>
                        <TableCell sx={{ color: GREY, fontSize: 12 }}>
                          {[...a.envs].join(', ')}
                          {a.envs.size === 1 && a.servers > 0 ? (
                            <Chip size="small" label="single environment" sx={{ ml: 1, bgcolor: '#FFF3E0', color: '#B26A00', fontWeight: 700 }} />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Stack>

          {coverage.singleEnvApps.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>{coverage.singleEnvApps.length} application(s) have servers in only ONE environment</AlertTitle>
              That is often a sign the other instances were missed rather than genuinely absent — a clinical app with a
              Production instance and no Test/Train is unusual.{' '}
              {coverage.singleEnvApps.slice(0, 12).map((a) => `${a.appName} (${[...a.envs][0]})`).join(', ')}
              {coverage.singleEnvApps.length > 12 ? ` … and ${coverage.singleEnvApps.length - 12} more.` : ''}
            </Alert>
          )}

          {coverage.zeroServerDeployments.length > 0 && (
            <Alert severity="info">
              <AlertTitle>{coverage.zeroServerDeployments.length} deployment(s) have zero servers</AlertTitle>
              Either the instance is SaaS/vendor-hosted, or its servers were never mapped:{' '}
              {coverage.zeroServerDeployments.slice(0, 15).map((d) => d.name).join(', ')}
              {coverage.zeroServerDeployments.length > 15 ? ` … and ${coverage.zeroServerDeployments.length - 15} more.` : ''}
            </Alert>
          )}

          {/* ---- provenance ------------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Where every mapping came from
              </Typography>
              <Button size="small" startIcon={<DownloadIcon />} onClick={exportProvenance} disabled={!coverage.rows.length}>
                Export provenance CSV
              </Button>
            </Stack>
            <Table size="small">
              <TableHead><TableRow sx={headSx}>
                <TableCell>Source</TableCell><TableCell>Rule / worksheet</TableCell>
                <TableCell align="right">Servers mapped</TableCell><TableCell>Confidence</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {coverage.byRule.map((r, i) => (
                  <TableRow key={`${r.source}-${r.rule}-${i}`} hover>
                    <TableCell><SourceChip source={r.source} /></TableCell>
                    <TableCell sx={{ fontSize: 12 }}><code>{r.rule}</code></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{r.count.toLocaleString()}</TableCell>
                    <TableCell>{r.confidence === undefined ? <span style={{ color: GREY }}>not recorded</span> : <ConfidenceChip value={r.confidence} />}</TableCell>
                  </TableRow>
                ))}
                {!coverage.byRule.length && (
                  <TableRow><TableCell colSpan={4} sx={{ color: GREY }}>Nothing is mapped yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" sx={{ color: GREY }}>
              Server-level detail — the rule that produced each individual link, so a bad rule can be traced and reversed.
            </Typography>
            <Table size="small">
              <TableHead><TableRow sx={headSx}>
                <TableCell>Server</TableCell><TableCell>Deployment</TableCell><TableCell>Application</TableCell>
                <TableCell>Environment</TableCell><TableCell>Source</TableCell><TableCell>Rule</TableCell>
                <TableCell>Confidence</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {coverage.rows.slice((provPage - 1) * PAGE_SIZE, provPage * PAGE_SIZE).map((r) => (
                  <TableRow key={`${r.serverId}-${r.depId}`} hover>
                    <TableCell>
                      <Box component="span" sx={{ cursor: onOpen ? 'pointer' : 'default', color: onOpen ? BLUE : 'inherit' }}
                        onClick={() => onOpen && onOpen(r.serverId)}>{r.serverName}</Box>
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={{ cursor: onOpen ? 'pointer' : 'default', color: onOpen ? BLUE : 'inherit' }}
                        onClick={() => onOpen && onOpen(r.depId)}>{r.depName}</Box>
                    </TableCell>
                    <TableCell sx={{ color: GREY }}>{r.appName}</TableCell>
                    <TableCell sx={{ color: GREY }}>{r.env}</TableCell>
                    <TableCell><SourceChip source={r.source} /></TableCell>
                    <TableCell sx={{ fontSize: 12, color: GREY, maxWidth: 260, wordBreak: 'break-all' }}>{r.rule || '(not recorded)'}</TableCell>
                    <TableCell>{r.confidence === undefined ? <span style={{ color: GREY }}>—</span> : <ConfidenceChip value={r.confidence} />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager count={coverage.rows.length} page={provPage} onChange={setProvPage} />
          </Paper>

          {/* ---- decommission candidates ------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Likely decommission candidates — {coverage.decomm.length.toLocaleString()}
              </Typography>
              <Button size="small" startIcon={<DownloadIcon />} disabled={!coverage.decomm.length}
                onClick={() => exportUnmapped(coverage.decomm, 'decommission-candidates')}>
                Export CSV
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ color: GREY }}>
              Flagged purely on naming (&quot;-old&quot;, &quot;retired-2025-06-10&quot;, &quot;PlaceHolder&quot;). This is a
              prompt for a human, not a decision: nothing here is retired, excluded from a wave, or removed from any
              count. A box named &quot;old&quot; that is still serving traffic is exactly the kind of thing that breaks a
              cutover.
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow sx={headSx}>
                <TableCell>Server</TableCell><TableCell>Signal</TableCell><TableCell>Cluster</TableCell>
                <TableCell>Currently mapped to</TableCell><TableCell align="right">vCPU</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {coverage.decomm.slice((decommPage - 1) * PAGE_SIZE, decommPage * PAGE_SIZE).map((e) => {
                  const prior = existingByServer.get(e.id);
                  return (
                    <TableRow key={e.id} hover>
                      <TableCell>
                        <Box component="span" sx={{ cursor: onOpen ? 'pointer' : 'default', color: onOpen ? BLUE : 'inherit' }}
                          onClick={() => onOpen && onOpen(e.id)}>{e.name}</Box>
                      </TableCell>
                      <TableCell sx={{ color: GREY, fontSize: 12 }}>{e.decomm.join(', ')}</TableCell>
                      <TableCell sx={{ color: GREY }}>{e.text.cluster}</TableCell>
                      <TableCell sx={{ color: GREY }}>{prior ? prior.depName : <span style={{ color: '#C62828' }}>unmapped</span>}</TableCell>
                      <TableCell align="right">{(e.s.fields || {}).vcpu || ''}</TableCell>
                    </TableRow>
                  );
                })}
                {!coverage.decomm.length && (
                  <TableRow><TableCell colSpan={5} sx={{ color: GREY }}>No decommission-style names found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <Pager count={coverage.decomm.length} page={decommPage} onChange={setDecommPage} />
          </Paper>
        </Stack>
      )}

      {/* ==================================================================== */}
      {/* TAB 4 — WORKSHEET ROUND-TRIP                                         */}
      {/* ==================================================================== */}
      {tab === 3 && (
        <Stack spacing={2}>
          <Alert severity="info">
            <AlertTitle>For the mapping that rules will never get right</AlertTitle>
            Most of this estate has no environment signal in the hostname, so a large share of the answer only exists in
            someone&apos;s head. Export the worksheet, let them fill it in — including off paper — and send it back. The
            round-trip is keyed on each server&apos;s fact-sheet id, so re-importing <b>updates</b> servers and never
            duplicates them, and you see a full diff before anything is written.
          </Alert>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportWorksheet}
                disabled={!index.length} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
                Export worksheet ({index.length.toLocaleString()} servers)
              </Button>
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current && fileRef.current.click()}
                sx={{ borderColor: BLUE, color: BLUE }}>
                Upload completed worksheet
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onWorksheetFile} />
              {wsFile && <Typography variant="body2" sx={{ color: GREY }}>{wsFile}</Typography>}
            </Stack>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
              The export pre-fills Application and Environment wherever a mapping already exists, so exporting and
              re-importing an untouched file is a no-op rather than a mass unmapping. Column A carries the server key
              and is hidden; if the client rebuilds the sheet by hand we fall back to matching on server name and tell
              you how many rows matched which way.
            </Typography>
          </Paper>

          {wsErr && <Alert severity="error">{wsErr}</Alert>}

          {wsDiff && (
            <>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Matched by key" value={wsDiff.counts.key.toLocaleString()} sub="identity — safe" />
                <Kpi label="Matched by name" value={wsDiff.counts.name.toLocaleString()} sub="fallback" tone={wsDiff.counts.name ? 'warn' : undefined} />
                <Kpi label="Matched nothing" value={wsDiff.counts.none.toLocaleString()} sub="nothing created for these" tone={wsDiff.counts.none ? 'bad' : undefined} />
                <Kpi label="Unchanged" value={wsDiff.counts.unchanged.toLocaleString()} />
                <Kpi label="Newly mapped" value={wsDiff.counts.new.toLocaleString()} />
                <Kpi label="Mapping changed" value={wsDiff.counts.changed.toLocaleString()} tone={wsDiff.counts.changed ? 'warn' : undefined} />
                <Kpi label="Rejected rows" value={(wsDiff.counts.unknownApp + wsDiff.counts.dupe).toLocaleString()} sub="unknown app / duplicate" />
              </Stack>

              {wsDiff.counts.none > 0 && (
                <Alert severity="warning">
                  {wsDiff.counts.none} row(s) matched no server in this workspace — by key or by name. They are listed
                  below and <b>nothing will be created for them</b>: this screen never invents a second server record
                  for a row it could not place.
                </Alert>
              )}

              {wsDiff.unknownEnvs.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle>{wsDiff.unknownEnvs.length} unrecognised Environment value(s)</AlertTitle>
                  {wsDiff.unknownEnvs.slice(0, 10).map((u) => `"${u.value}" (${u.count})`).join(', ')}
                  {wsDiff.unknownEnvs.length > 10 ? ' …' : ''}
                  <FormControlLabel
                    sx={{ display: 'block', mt: 1 }}
                    control={<Checkbox size="small" checked={wsKeepOther}
                      onChange={(e) => {
                        setWsKeepOther(e.target.checked);
                        if (lastWorkbook.current) {
                          buildWorksheetDiff(lastWorkbook.current.wb, lastWorkbook.current.fileName, e.target.checked);
                        }
                      }} />}
                    label={<Typography variant="body2">
                      Keep these verbatim as <b>environment = Other</b> with the typed text in <b>environmentLabel</b>.
                      Untick to reject those rows instead. Either way nothing is silently coerced or dropped —
                      &quot;Bob&apos;s build&quot; is real information about a real instance.
                    </Typography>}
                  />
                </Alert>
              )}

              {wsDiff.unknownApps.length > 0 && (
                <Alert severity="error">
                  <AlertTitle>{wsDiff.unknownApps.length} unrecognised Application name(s) — those rows are rejected</AlertTitle>
                  {wsDiff.unknownApps.slice(0, 10).map((u) => `"${u.value}" (${u.count})`).join(', ')}
                  {wsDiff.unknownApps.length > 10 ? ' …' : ''}. No application fact sheet is created from a worksheet —
                  create it first, then re-upload.
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                    Diff — nothing is written until you apply
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField select size="small" value={wsFilter} onChange={(e) => { setWsFilter(e.target.value); setWsPage(1); }} sx={{ minWidth: 190 }}>
                      <MenuItem value="all">All rows ({wsDiff.rows.length})</MenuItem>
                      <MenuItem value="new">Newly mapped ({wsDiff.counts.new})</MenuItem>
                      <MenuItem value="changed">Changed ({wsDiff.counts.changed})</MenuItem>
                      <MenuItem value="unchanged">Unchanged ({wsDiff.counts.unchanged})</MenuItem>
                      <MenuItem value="nomatch">Matched nothing ({wsDiff.counts.none})</MenuItem>
                      <MenuItem value="invalid">Rejected</MenuItem>
                    </TextField>
                    <Button variant="contained" disabled={!wsApplicable.length || applying}
                      onClick={applyWorksheet} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
                      Apply {wsApplicable.length.toLocaleString()} row{wsApplicable.length === 1 ? '' : 's'}
                    </Button>
                  </Stack>
                </Stack>

                {(() => {
                  const filtered = wsDiff.rows.filter((r) => {
                    if (wsFilter === 'all') return true;
                    if (wsFilter === 'invalid') return r.status === 'invalid' || r.status === 'duplicate';
                    return r.status === wsFilter;
                  });
                  const page = filtered.slice((wsPage - 1) * PAGE_SIZE, wsPage * PAGE_SIZE);
                  return (
                    <>
                      <Table size="small">
                        <TableHead><TableRow sx={headSx}>
                          <TableCell>Row</TableCell><TableCell>Server</TableCell><TableCell>Matched by</TableCell>
                          <TableCell>Status</TableCell><TableCell>Current</TableCell><TableCell>Proposed</TableCell>
                          <TableCell>Note</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {page.map((r, i) => (
                            <TableRow key={`${r.rowIdx}-${i}`} hover>
                              <TableCell sx={{ color: GREY }}>{r.rowIdx}</TableCell>
                              <TableCell>{r.entry ? r.entry.name : (r.rawName || <span style={{ color: GREY }}>(blank)</span>)}</TableCell>
                              <TableCell>
                                {r.matchedBy === 'key' && <Chip size="small" label="key" variant="outlined" sx={{ borderColor: '#2E7D32', color: '#2E7D32' }} />}
                                {r.matchedBy === 'name' && <Chip size="small" label="name" variant="outlined" sx={{ borderColor: '#B26A00', color: '#B26A00' }} />}
                                {r.matchedBy === 'none' && <Chip size="small" label="none" variant="outlined" sx={{ borderColor: '#C62828', color: '#C62828' }} />}
                              </TableCell>
                              <TableCell>
                                {r.status === 'new' && <Chip size="small" label="new" color="success" variant="outlined" />}
                                {r.status === 'changed' && <Chip size="small" label="changed" sx={{ borderColor: '#B26A00', color: '#B26A00' }} variant="outlined" />}
                                {r.status === 'unchanged' && <Chip size="small" label="unchanged" variant="outlined" />}
                                {r.status === 'blank' && <Chip size="small" label="blank" variant="outlined" />}
                                {r.status === 'nomatch' && <Chip size="small" label="no server" color="error" variant="outlined" />}
                                {r.status === 'invalid' && <Chip size="small" label="rejected" color="error" variant="outlined" />}
                                {r.status === 'duplicate' && <Chip size="small" label="duplicate row" color="error" variant="outlined" />}
                              </TableCell>
                              <TableCell sx={{ color: GREY, fontSize: 12 }}>
                                {r.old ? `${r.old.appName} · ${r.old.env}` : '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>
                                {r.appName ? `${r.appName} · ${r.environment}${r.environmentLabel ? ` — ${r.environmentLabel}` : ''}` : '—'}
                              </TableCell>
                              <TableCell sx={{ color: GREY, fontSize: 12 }}>{r.issue || r.coerced || ''}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Pager count={filtered.length} page={wsPage} onChange={setWsPage} />
                    </>
                  );
                })()}
              </Paper>
            </>
          )}
        </Stack>
      )}

      {/* ---- confirm dialog --------------------------------------------- */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#F5A623' }} /> Confirm apply
        </DialogTitle>
        <DialogContent dividers>
          {confirm && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>{confirm.total.toLocaleString()}</b> server link{confirm.total === 1 ? '' : 's'} will be written, and
                Deployment fact sheets will be created or reused for each application + environment group.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: GREY }}>
                Every link is stamped with its source, the exact rule that produced it, a confidence and a timestamp, so
                it can be traced and reversed later.
              </Typography>
              {confirm.conflicts > 0 ? (
                <Alert severity="warning">
                  <AlertTitle>{confirm.conflicts.toLocaleString()} server(s) are already mapped somewhere else</AlertTitle>
                  {confirm.kind === 'rules'
                    ? 'You ticked "Overwrite" — those servers will be MOVED off their current deployment onto the new one. Untick it to skip them instead.'
                    : 'A person filled these in on the worksheet, so their answer replaces the current mapping. Those servers will be moved off their current deployment.'}
                </Alert>
              ) : (
                <Alert severity="info">No existing mapping will be changed.</Alert>
              )}
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1.5 }}>
                &quot;Revert last apply&quot; will undo this whole operation.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={doApply} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
